import { c as _c } from "react/compiler-runtime";
import CubeIcon from "@assets/icons/cube.svg?react";
import LocationWorldIcon from "@assets/icons/location-world.svg?react";
import { useMapNavigation } from "@components/world-map/hooks/use-map-navigation";
import { MapStyleThumbnail } from "@components/world-map/map-style-thumbnail";
import { WorldMapButtons } from "@components/world-map/world-map-buttons";
import { isWorldMap2DModeEnabled, WORLD_MAP_CONTROL_ICON_SIZE } from "@components/world-map/world-map-constants";
import { Banner, GpsFixedIcon, LocationMapIcon, MinusIcon, PlusIcon, HomeIcon } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { WorldMapEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
/**
 * Renders map navigation controls (zoom, location, reset-view, style toggle)
 * and the location permission banner.
 * Must only be rendered after the map view is ready.
 */
export function WorldMapControls(t0) {
  const $ = _c(23);
  const {
    viewRef,
    mapContext,
    shouldShowMapControls,
    viewAllEntities,
    mapStyle,
    setMapStyle,
    mapMode,
    setMapMode,
    onResetView,
    externalButtons
  } = t0;
  const navigation = useMapNavigation(viewRef, true, mapContext);
  const {
    trackEvent
  } = useTrackEvent();
  let t1;
  if ($[0] !== mapContext || $[1] !== trackEvent) {
    t1 = function trackControl(control, controlValue) {
      trackEvent({
        name: WorldMapEvents.useMapControl,
        props: {
          control,
          mapContext,
          ...(controlValue && {
            controlValue
          })
        }
      });
    };
    $[0] = mapContext;
    $[1] = trackEvent;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const trackControl = t1;
  let t2;
  if ($[3] !== onResetView || $[4] !== trackControl || $[5] !== viewAllEntities) {
    t2 = function handleResetView() {
      trackControl("reset view");
      viewAllEntities();
      onResetView?.();
    };
    $[3] = onResetView;
    $[4] = trackControl;
    $[5] = viewAllEntities;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleResetView = t2;
  let t3;
  if ($[7] !== externalButtons || $[8] !== handleResetView || $[9] !== mapMode || $[10] !== mapStyle || $[11] !== navigation || $[12] !== setMapMode || $[13] !== setMapStyle || $[14] !== shouldShowMapControls || $[15] !== trackControl) {
    const buttonGroups = shouldShowMapControls ? getFullControlGroups({
      navigation,
      trackControl,
      viewAllEntities: handleResetView,
      mapStyle,
      setMapStyle,
      mapMode,
      setMapMode
    }) : getMinimalControlGroups(handleResetView);
    const externalGroups = (externalButtons ?? []).map(_temp);
    t3 = <WorldMapButtons buttonGroups={[...externalGroups, ...buttonGroups]} />;
    $[7] = externalButtons;
    $[8] = handleResetView;
    $[9] = mapMode;
    $[10] = mapStyle;
    $[11] = navigation;
    $[12] = setMapMode;
    $[13] = setMapStyle;
    $[14] = shouldShowMapControls;
    $[15] = trackControl;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== navigation.dismissLocationBanner || $[18] !== navigation.shouldShowLocationBanner) {
    t4 = navigation.shouldShowLocationBanner && <Box sx={{
      position: "absolute",
      top: 2,
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 1000,
      maxWidth: "90%"
    }}><Banner variant="warning" title="Sphere XG does not have permission to use your location." onClose={navigation.dismissLocationBanner} /></Box>;
    $[17] = navigation.dismissLocationBanner;
    $[18] = navigation.shouldShowLocationBanner;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== t3 || $[21] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return t5;
}

/** Returns a single "Reset view" button group for minimal mode. */
function _temp(button) {
  return [button];
}
function getMinimalControlGroups(viewAllEntities) {
  return [[{
    icon: <HomeIcon sx={{
      width: WORLD_MAP_CONTROL_ICON_SIZE,
      height: WORLD_MAP_CONTROL_ICON_SIZE
    }} />,
    tooltip: "Reset view",
    onClick: viewAllEntities
  }]];
}

/** Returns the full set of navigation button groups. */
function getFullControlGroups({
  navigation,
  trackControl,
  viewAllEntities,
  mapStyle,
  setMapStyle,
  mapMode,
  setMapMode
}) {
  const mainGroup = [{
    icon: <HomeIcon sx={{
      width: WORLD_MAP_CONTROL_ICON_SIZE,
      height: WORLD_MAP_CONTROL_ICON_SIZE
    }} />,
    tooltip: "Reset view",
    onClick: viewAllEntities
  }, {
    icon: <PlusIcon sx={{
      width: WORLD_MAP_CONTROL_ICON_SIZE,
      height: WORLD_MAP_CONTROL_ICON_SIZE
    }} />,
    tooltip: "Zoom in",
    onClick: () => {
      trackControl("zoom in");
      navigation.zoomIn();
    },
    isDisabled: navigation.isZoomInDisabled
  }, {
    icon: <MinusIcon sx={{
      width: WORLD_MAP_CONTROL_ICON_SIZE,
      height: WORLD_MAP_CONTROL_ICON_SIZE
    }} />,
    tooltip: "Zoom out",
    onClick: () => {
      trackControl("zoom out");
      navigation.zoomOut();
    },
    isDisabled: navigation.isZoomOutDisabled
  }];

  // Tilt toggle (3D only): snaps between top-down and an angled view, Apple-Maps
  // style. The button is highlighted (blue cube icon + light background) while the
  // camera is tilted, signalling the active 3D perspective; the tooltip stays
  // fixed regardless of state. The active state follows the live camera tilt, so
  // it stays in sync when the user tilts by hand (manual tilt is not locked).
  if (navigation.canTilt) {
    mainGroup.push({
      icon: <CubeIcon />,
      tooltip: "Tilt (3D)",
      onClick: () => {
        trackControl("tilt", navigation.isTilted ? "top down" : "angled");
        navigation.toggleTilt();
      },
      isActive: navigation.isTilted
    });
  }
  const groups = [[{
    icon: <GpsFixedIcon sx={{
      width: WORLD_MAP_CONTROL_ICON_SIZE,
      height: WORLD_MAP_CONTROL_ICON_SIZE
    }} />,
    tooltip: navigation.isLocationDisabled ? "Cannot access your location" : "Current location",
    onClick: () => {
      trackControl("current location");
      navigation.goToCurrentLocation();
    },
    isDisabled: navigation.isLocationDisabled
  }], mainGroup, [{
    icon: <MapStyleThumbnail mapStyle={mapStyle} />,
    tooltip: mapStyle === "colored" ? "Switch to satellite" : "Switch to map",
    onClick: () => {
      const isSwitchingToSatellite = mapStyle === "colored";
      trackControl("map style", isSwitchingToSatellite ? "satellite" : "map");
      setMapStyle(isSwitchingToSatellite ? "satellite" : "colored");
    }
  }]];

  // The 2D/3D toggle is only shown while the 2D mode is enabled. The product is
  // currently 3D-only, so this button is hidden (the code path is kept behind
  // the flag; see isWorldMap2DModeEnabled).
  if (isWorldMap2DModeEnabled) {
    groups.push([{
      icon: mapMode === "3D" ? <LocationMapIcon /> : <LocationWorldIcon />,
      tooltip: mapMode === "3D" ? "Switch to 2D" : "Switch to 3D",
      onClick: () => setMapMode(mapMode === "3D" ? "2D" : "3D")
    }]);
  }
  return groups;
}